import { useCallback, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSettingStore } from "@/store/setting-store";
import { useBackButton } from "@/store/use-back-button";
import Icon from "./icon";

export type TopBarProps = {
  title?: string;
  href?: string;
  altHref?: string;
  closeButton?: boolean;
  sticky?: boolean;
  noBody?: boolean;
  /** Hides the UI while keeping the close/back behavior active. */
  bare?: boolean;
  onBackClick?: () => void;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
};

type CloseButtonProps = Omit<TopBarProps, "closeButton">;

export const CloseButton = ({
  href,
  title,
  onBackClick,
  altHref,
  sticky,
  noBody = false,
  bare = false,
  subtitle,
  actions,
  className = "",
}: CloseButtonProps) => {
  const navigate = useNavigate();
  const { isFullscreen } = useSettingStore();

  const handleBackClick = useCallback(() => {
    if (onBackClick) {
      onBackClick();
      return;
    }

    if (href) {
      navigate(href, { replace: true });
      return;
    }

    const idx =
      (window.history.state as { idx?: number } | null)?.idx ?? 0;

    if (idx > 0) {
      navigate(-1);
    } else if (altHref) {
      navigate(altHref, { replace: true });
    } else {
      navigate(-1);
    }
  }, [navigate, href, altHref, onBackClick]);

  useBackButton(handleBackClick, Boolean(title || href || onBackClick));

  // Keep the component mounted so its back-button behavior remains active,
  // while rendering no visible UI in bare mode.
  if (bare || noBody === true) {
    return null;
  }

  const heightClass = (title || isFullscreen) ? "h-12" : "";
  const paddingClass = "px-4";

  const fixedClass = `w-full z-30 ${heightClass} ${paddingClass} flex gap-1 ${isFullscreen ? "justify-center" : ""
    } items-center transition duration-200 bg-transparent ${className}`;

  // Sticky mode pins the bar inside its scroll container and lays a blurred
  // backdrop so the content scrolling underneath stays readable. Non-sticky
  // keeps the original transparent inline behavior.
  const safeAreaClass = "safe-area-inset-top";

  const wrapperClass = (sticky && !isFullscreen)
    ? `${safeAreaClass} w-full sticky top-0 z-30 bg-inherit backdrop-blur-sm`
    : `${safeAreaClass} w-full`;


  return (
    <div className={wrapperClass}>
      <div className={fixedClass}>
        {isFullscreen ? (
          title && title !== "بازگشت" ? (
            <span className="text-sm font-semibold text-foreground">{title}</span>
          ) : null
        ) : (
          <>
            {(title || href || onBackClick) && (
              <button
                type="button"
                onClick={handleBackClick}
                aria-label="back"
                className="shrink-0"
              >
                <Icon name="arrow-right" size={20} />
              </button>
            )}

            {(title || subtitle) && (
              <div className="min-w-0 flex-1">
                {title && (
                  <span className="block truncate text-sm font-semibold text-foreground lg:text-base">
                    {title}
                  </span>
                )}
                {subtitle && (
                  <span className="mt-0.5 hidden truncate text-xs text-default-500 lg:block">
                    {subtitle}
                  </span>
                )}
              </div>
            )}

            {actions}
          </>
        )}
      </div>
    </div>
  );
};

export const SafeArea = () => (
  <div className={`w-full flex flex-col gap-4 safe-area-inset-top`}>
    <div className="content-safe-area-inset-top" />
  </div>
);

const TopBar = ({ closeButton = false, bare = false, ...props }: TopBarProps) => {
  if (bare) return <CloseButton {...props} bare />;

  return closeButton ? <SafeArea /> : <CloseButton {...props} />;
};

export default TopBar;
